import Button, { Colors } from "@common/components/Button";
import Grid from '@mui/material/Grid2';
import { SearchField } from "@common/components/Header/SearchBar/Search/Field";
import { allFields } from "@common/components/Header/SearchBar/Search/const";
import RightDrawer from "@common/components/RightDrawer";
import { FiltersTypeImpl } from "@common/utils/filters";
import { DeviceFieldValueProvider } from "@pages/Dashboard/WidgetEdit/DeviceFieldValueContext";
import TimeButton from "@pages/Dashboard/WidgetEdit/FilterPanel/FilterSections/Section/components/time";
import { useUserSelectedAccounts } from "@services/account/hooks/useUserSelectedAccounts";
import { getAssignees } from "@services/meetings";
import { Assignee } from "@services/meetings/assignee";
import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { countOtherFilters as countOtherFiltersFunc } from "../helpers";
import { useParams, matchPath, useLocation } from "react-router-dom";
import { EntityTypeList } from "@pages/BI/Audit/const";
import mappers from "@common/utils/filters/mappers";
import { useAuthenticatedUser } from "@services/user/hooks/useAuthenticatedUser";
import { hasAssetTaggingLicense } from "@common/utils/user";

type AddFiltersDrawerProps<
  T extends UserWidgetType | UserOtherType = UserWidgetType | UserOtherType
> = {
  open: boolean;
  onClose: (filters: FilterDefinition[] | undefined) => void;
  filters: FilterDefinition[];
  handleClickFilter: (
    filters: FilterDefinition[],
    filterType: FilterTypes,
    filterValue: string,
    payload?: FilterDefinition["payload"]
  ) => FilterDefinition[];
  type: T;
  handleClickClear: () => void;
  entity?: { entityType?: EntityTypeList[] | EntityTypeList };
};

const AddFiltersDrawer = (props: AddFiltersDrawerProps) => {
  const { open, onClose, filters, handleClickFilter, type, handleClickClear } =
    props;
  const { t } = useTranslation();
  const [tempFilters, setTempFilters] = useState<FilterDefinition[]>([]);
  const [producersList, setProducersList] = useState<Assignee[]>([]);
  const [, , allAccounts] = useUserSelectedAccounts();
  const { accountid, roomid, locationid, deviceid } = useParams();
  const { pathname } = useLocation();
  const [user] = useAuthenticatedUser();

  const fieldListToDisplay = useCallback(() => {
    const filterFields = (
      type: UserWidgetType | UserOtherType,
      excludeKeys: string[]
    ) => allFields[type].filter(({ key }) => !excludeKeys.includes(key));
    const hasTagsLicense = hasAssetTaggingLicense(user?.account);

    const getFilteredFieldsByType = (type: UserWidgetType | UserOtherType) => {
      switch (type) {
        case FiltersTypeImpl.Tickets:
          if (accountid) return filterFields(type, ["accountId"]);

          if (locationid)
            return filterFields(type, [
              "accountId",
              "room.location.country",
              "room.location.name",
            ]);

          if (roomid)
            return filterFields(type, [
              "accountId",
              "room.location.country",
              "room.location.name",
              "room.name",
            ]);

          if (deviceid)
            return filterFields(type, [
              "accountId",
              "room.location.country",
              "room.location.name",
              "room.name",
              "device.name",
              "device.type",
              "device.category",
              "device.manufacturer",
              "device.model",
            ]);
          break;

        case FiltersTypeImpl.Workflows:
          if (accountid) return filterFields(type, ["accountId"]);
          break;

        case FiltersTypeImpl.AuditTrails:
          if (pathname.includes("users"))
            return filterFields(type, ["accountId"]);

          if (!matchPath("/bi/*", pathname))
            return filterFields(type, ["accountId", "entityType"]);
          break;

        case FiltersTypeImpl.Accounts:
        case FiltersTypeImpl.Locations:
        case FiltersTypeImpl.Rooms:
        case FiltersTypeImpl.Devices:
          if (!hasTagsLicense) return filterFields(type, ["tags"]);
          break;

        default:
          break;
      }

      return allFields[type];
    };

    return getFilteredFieldsByType(type);
  }, [
    type,
    accountid,
    roomid,
    locationid,
    deviceid,
    pathname,
    allFields,
    user,
  ]);

  const timeFilters = tempFilters.filter((f) =>
    f.filterType.startsWith("time")
  );

  const assignedFilter = tempFilters.find(
    (f) => f.filterType === "assignee" && f.filterValue === "ASSIGNED_TO"
  );

  const targetKeys = [
    "category",
    "deviceType",
    "manufacturer",
    "model",
    "threshold.manufacturer",
    "threshold.deviceType",
    "threshold.category",
    "threshold.deviceModel",
  ] as (keyof TargetFilterPayload)[];

  const isTargetKey = (key: string) =>
    type !== FiltersTypeImpl.DeviceCatalog &&
    targetKeys.includes(key as keyof TargetFilterPayload);

  const uploadTargetPayload = (key: string, value: string) => {
    let updatedPayload: TargetFilterPayload = {};
    const targetFilter = tempFilters?.find(
      ({ filterType }) => filterType === "target"
    );
    updatedPayload = targetFilter
      ? {
          ...(targetFilter.payload as TargetFilterPayload),
          [key]: Boolean(value) ? value : undefined,
        }
      : {
          [key]: Boolean(value) ? value : undefined,
        };

    return updatedPayload;
  };
  useEffect(() => {
    if (open) {
      setTempFilters(filters);
      getProducersList();
    }
  }, [open]);

  const handleClick = (
    filterType: FilterTypes,
    filterValue: string,
    payload?: FilterDefinition["payload"]
  ) => {
    if (isTargetKey(filterType)) {
      const targetUpdatedPayload = uploadTargetPayload(filterType, filterValue);
      setTempFilters((filters) =>
        handleClickFilter(filters, "target", "", targetUpdatedPayload)
      );
    } else if (filterType === "assignee" && filterValue === "ME") {
      setTempFilters((filters) => {
        const producerIds = handleClickFilter(
          filters,
          "producerIds",
          "",
          payload
        );
        return handleClickFilter(producerIds, filterType, filterValue, payload);
      });
    } else {
      setTempFilters((filters) =>
        handleClickFilter(filters, filterType, filterValue, payload)
      );
    }
  };

  const handleSearch = useCallback(() => {
    onClose(
      tempFilters.filter((filter) => {
        if (
          !filter.filterType.startsWith("time") ||
          filter.filterValue !== "Custom time period"
        )
          return true;
        if (filter.payload === undefined) return false;
        return Object.values(filter.payload).every((x) => !isNaN(x));
      })
    );
  }, [tempFilters]);

  const getValue = (key: string) => {
    if (isTargetKey(key)) {
      const targetFilter = tempFilters.find((f) => f.filterType === "target");
      if (targetFilter) {
        const payload: Omit<TargetFilterPayload, "deviceType"> =
          targetFilter.payload as TargetFilterPayload;
        return Boolean(payload)
          ? payload[key as keyof Omit<TargetFilterPayload, "deviceType">]
          : "";
      }
    } else {
      const filter = tempFilters
        ?.filter(({ filterType }) => filterType !== "status")
        .find((f) => f.filterType === key);
      if (filter) return filter["filterValue"];
    }
    return "";
  };

  const getAutoCompleteValues = useCallback(
    (key: FilterTypes) => {
      if (type !== FiltersTypeImpl.DeviceCatalog && key === "deviceType") {
        const targetFilterPayload: TargetFilterPayload = tempFilters.find(
          (f) => f.filterType === "target"
        )?.payload as TargetFilterPayload;
        if (targetFilterPayload) {
          return (targetFilterPayload?.deviceType as string[]) ?? [];
        }
      } else {
        const filter = tempFilters?.find((f) => f.filterType === key);
        if (filter && Array.isArray(filter["payload"]))
          return filter["payload"] as string[];
      }

      return [];
    },
    [tempFilters, type]
  );

  const handleAutoComplete = (key: FilterTypes, values: string[]) => {
    if (values.length === 0) {
      handleSelectClear(key);
    } else if (type !== FiltersTypeImpl.DeviceCatalog && key === "deviceType") {
      handleClick("target", "", { deviceType: values });
    } else {
      handleClick(key as FilterTypes, "", values);
    }
  };

  const handleSelectClear = useCallback(
    (key: string) => {
      if (isTargetKey(key)) {
        const targetUpdatedPayload = uploadTargetPayload(key, "");
        setTempFilters([
          ...tempFilters?.filter((f) => f.filterType !== "target"),
          {
            filterType: "target",
            filterValue: "",
            payload: targetUpdatedPayload,
          },
        ]);
      } else if (key === "time" && FiltersTypeImpl.Meetings === type) {
        setTempFilters([
          ...tempFilters?.filter((f) => f.filterType !== "time"),
          {
            filterType: "time",
            filterValue: "For the next 12 hours",
            payload: {},
          },
        ]);
      } else setTempFilters(tempFilters?.filter((x) => x.filterType !== key));
    },
    [tempFilters]
  );

  const getProducersList = async () => {
    const producersList = await getAssignees();
    setProducersList(producersList);
  };

  const clearAllFilters = () => {
    let filters = [];

    if (allAccounts.length === 1) {
      filters.push({ filterType: "accountId", filterValue: allAccounts[0].id });
    }
    if (type === FiltersTypeImpl.Meetings) {
      filters.push({
        filterType: "time",
        filterValue: "For the next 12 hours",
      });
    }
    setTempFilters(filters);
    handleClickClear();
  };

  const filtersCount = countOtherFiltersFunc(tempFilters);
  return (
    <DeviceFieldValueProvider>
      <RightDrawer
        open={open}
        onClose={handleSearch}
        title={t("Add filters ({{ COUNTER }})", {
          COUNTER: filtersCount,
        })}
        closeButton={
          <Grid
            sx={(theme) => ({
              "&>.MuiButton-root": { marginRight: theme.spacing(1) },
            })}>
            <Button color="white" onClick={() => handleSearch()}>
              Filter
            </Button>
            <Button
              color="greyed"
              variant="outlined"
              onClick={() => clearAllFilters()}
            >
              Clear
            </Button>
            <Button
              color="greyed"
              variant="outlined"
              onClick={() => onClose(undefined)}
            >
              Cancel
            </Button>
          </Grid>
        }
      >
        <Grid
          container
          direction="column"
          wrap="nowrap"
          sx={(theme) => ({
            padding: theme.spacing(2),
            height: "100%",
            overflowY: "scroll",
            overflowX: "hidden",
          })}
        >
          <>
            {fieldListToDisplay()
              .sort((a, b) =>
                a.label.toLowerCase().localeCompare(b.label.toLowerCase())
              )
              .map(({ key, label, filterDefKey }) => (
                <React.Fragment key={key}>
                  <SearchField
                    entity={
                      props.entity ?? type === "Devices"
                        ? mappers[type].fromDefinition.toSL(tempFilters)
                        : undefined
                    }
                    entityType={props.entity?.entityType}
                    value={getValue(filterDefKey ?? key)}
                    fullWidth
                    margin="dense"
                    property={key}
                    label={t(label)}
                    onChange={(evt) =>
                      handleClick(
                        (filterDefKey ?? key) as FilterTypes,
                        evt.target.value
                      )
                    }
                    filterType={type}
                    onEnterKey={handleSearch}
                    getAutoCompleteValues={getAutoCompleteValues}
                    handleAutoComplete={handleAutoComplete}
                    handleClickSelectClear={() =>
                      handleSelectClear(filterDefKey ?? key)
                    }
                    getProducersList={getProducersList}
                    producersList={producersList}
                  />

                  {key.startsWith("time") &&
                    timeFilters.some(
                      (f) =>
                        f.filterType === key &&
                        f.filterValue === "Custom time period"
                    ) && (
                      <TimeButton
                        buttonProps={{
                          chip: true,
                          activeColor: "white" as Colors,
                          onClick: () =>
                            handleClick(
                              key as FilterTypes,
                              "Custom time period"
                            ),
                          active:
                            tempFilters.some(
                              (f) =>
                                f.filterType === key &&
                                f.filterValue === "Custom time period"
                            ) ?? false,
                          children: "Choose the time period",
                        }}
                        key={"Custom time period"}
                        onFilterChange={handleClick}
                        filters={tempFilters}
                        filterType={type}
                        filterTypeKey={key as FilterTypes}
                      />
                    )}
                  {key === "assignee" && Boolean(assignedFilter) && (
                    <SearchField
                      value={getValue(filterDefKey ?? key)}
                      fullWidth
                      margin="dense"
                      property={"producerIds"}
                      label={t("Assigned to")}
                      key={"producerIds"}
                      onChange={(evt) =>
                        handleClick(
                          "producerIds" as FilterTypes,
                          evt.target.value
                        )
                      }
                      filterType={type}
                      getAutoCompleteValues={getAutoCompleteValues}
                      handleAutoComplete={handleAutoComplete}
                      onEnterKey={handleSearch}
                      handleClickSelectClear={handleSelectClear}
                      isAssignedTo={Boolean(assignedFilter)}
                      getProducersList={getProducersList}
                      producersList={producersList}
                    />
                  )}
                </React.Fragment>
              ))}
          </>
        </Grid>
      </RightDrawer>
    </DeviceFieldValueProvider>
  );
};

export default AddFiltersDrawer;
